import React, { useState } from 'react';
import { TrendingUp, Home, Building2, Banknote, PiggyBank, Landmark, CreditCard as DebtIcon, Lightbulb, ArrowUpRight, ShoppingCart } from 'lucide-react';
import { formatCurrency } from '../utils/helpers';
import { NetWorthChart } from '../components/NetWorthChart';
import { mockNetWorthChartData, mockTransactions, mockDividends, mockBusinesses, mockDividendPortfolio } from '../utils/mockData';

const mockNetWorth = 152840;
const mockNetWorthChange = 0.024;
const mockAssets = [
  { name: 'Cash', value: 42650, icon: Banknote, color: 'text-blue-600', bar: 'bg-blue-200' },
  { name: 'Investments', value: 78210, icon: PiggyBank, color: 'text-emerald-600', bar: 'bg-emerald-200' },
  { name: 'Dividends', value: 25000, icon: TrendingUp, color: 'text-purple-600', bar: 'bg-purple-200' },
  { name: 'Business', value: 15000, icon: Building2, color: 'text-orange-600', bar: 'bg-orange-200' },
  { name: 'Real Estate', value: 350000, icon: Landmark, color: 'text-blue-700', bar: 'bg-blue-100' },
  { name: 'Debts', value: -318020, icon: DebtIcon, color: 'text-rose-600', bar: 'bg-rose-200' },
];

// Income breakdown calculation from real data
const now = new Date();
const oneYearAgo = new Date(now);
oneYearAgo.setFullYear(now.getFullYear() - 1);
const incomeTx = mockTransactions.filter(
  (tx) => tx.type === 'income' && new Date(tx.date) >= oneYearAgo
);
const annualIncome = incomeTx.reduce((sum, tx) => sum + tx.amount, 0);
const breakdowns = [
  { label: 'Hourly', value: annualIncome / (52 * 40) },
  { label: 'Daily', value: annualIncome / 365 },
  { label: 'Weekly', value: annualIncome / 52 },
  { label: 'Monthly', value: annualIncome / 12 },
];

// Map mockTransactions to UI-friendly objects
const txIconMap: Record<string, any> = {
  Salary: TrendingUp,
  Freelance: PiggyBank,
  Rent: Building2,
  Food: ShoppingCart,
  Debt: DebtIcon,
  Cash: Banknote,
  Investment: PiggyBank,
  RealEstate: Landmark,
  default: Home,
};
const txColorMap: Record<string, string> = {
  Salary: 'text-emerald-600',
  Freelance: 'text-emerald-500',
  Rent: 'text-blue-600',
  Food: 'text-purple-500',
  Debt: 'text-rose-600',
  Cash: 'text-blue-500',
  Investment: 'text-emerald-600',
  RealEstate: 'text-blue-700',
  default: 'text-neutral-400',
};
const txBgMap: Record<string, string> = {
  Salary: 'bg-emerald-50',
  Freelance: 'bg-emerald-50',
  Rent: 'bg-blue-50',
  Food: 'bg-purple-50',
  Debt: 'bg-rose-50',
  Cash: 'bg-blue-50',
  Investment: 'bg-emerald-50',
  RealEstate: 'bg-blue-50',
  default: 'bg-neutral-100',
};
const mappedTransactions = mockTransactions.map((tx) => {
  const icon = txIconMap[tx.category || 'default'] || txIconMap.default;
  const color = txColorMap[tx.category || 'default'] || txColorMap.default;
  const bg = txBgMap[tx.category || 'default'] || txBgMap.default;
  return {
    ...tx,
    icon,
    color,
    bg,
    label: tx.description || tx.category || 'Transaction',
    date: tx.date,
  };
});

export const HomePage: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<'hour' | 'day' | 'week' | 'month'>('week');

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold text-neutral-700 dark:text-neutral-300 mb-1">SimpleTracker</h1>
          <div className="flex items-end gap-4">
            <div>
              <div className="text-4xl font-bold text-neutral-900 dark:text-neutral-100">{formatCurrency(mockNetWorth)}</div>
              <div className="text-xs text-neutral-500 dark:text-neutral-400">Net Worth</div>
            </div>
            <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-semibold text-sm">
              <ArrowUpRight className="w-4 h-4" />
              <span>+{(mockNetWorthChange * 100).toFixed(1)}%</span>
            </div>
          </div>
        </div>
        <div className="flex gap-2 mt-2 md:mt-0">
          {['hour', 'day', 'week', 'month'].map((period) => (
            <button
              key={period}
              onClick={() => setSelectedPeriod(period as any)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition shadow-sm ${selectedPeriod === period ? 'bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900' : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-700'}`}
            >
              {period.charAt(0).toUpperCase() + period.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Net Worth Chart */}
      <div className="bg-white dark:bg-gray-800 border border-neutral-200 dark:border-neutral-700 rounded-xl p-6 shadow-sm">
        <NetWorthChart data={mockNetWorthChartData[selectedPeriod]} period={selectedPeriod} />
      </div>

      {/* Income Breakdown Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl mx-auto">
        {breakdowns.map((item) => (
          <div
            key={item.label}
            className="bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex flex-col items-center justify-center py-6 shadow-sm"
          >
            <div className="text-2xl font-semibold text-emerald-700 dark:text-emerald-400 mb-1">
              {formatCurrency(item.value)}
            </div>
            <div className="text-base text-emerald-700 dark:text-emerald-400 font-medium">
              {item.label}
            </div>
          </div>
        ))}
      </div>

      {/* Assets & Liabilities */}
      <div>
        <h2 className="text-base font-semibold text-neutral-800 dark:text-neutral-200 mb-2">Assets & Liabilities</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {mockAssets.map((asset) => (
            <div key={asset.name} className="bg-white dark:bg-gray-800 border border-neutral-100 dark:border-neutral-700 rounded-lg p-4 shadow-sm flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <asset.icon className={`w-5 h-5 ${asset.color}`} />
                <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">{asset.name}</span>
              </div>
              <div className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">{formatCurrency(asset.value)}</div>
              <div className={`h-1 rounded-full ${asset.bar} w-full`} />
            </div>
          ))}
        </div>
      </div>

      {/* Dividend & Business Income */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Dividend Portfolio */}
        <div className="bg-white dark:bg-gray-800 border border-neutral-100 dark:border-neutral-700 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Dividend Portfolio</h3>
            <TrendingUp className="w-5 h-5 text-purple-600" />
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-neutral-600 dark:text-neutral-400">Portfolio Value</span>
              <span className="font-semibold text-neutral-900 dark:text-neutral-100">{formatCurrency(mockDividendPortfolio.total_value)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-neutral-600 dark:text-neutral-400">Annual Income</span>
              <span className="font-semibold text-green-600">{formatCurrency(mockDividendPortfolio.annual_dividend_income)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-neutral-600 dark:text-neutral-400">Dividend Yield</span>
              <span className="font-semibold text-neutral-900 dark:text-neutral-100">{(mockDividendPortfolio.dividend_yield * 100).toFixed(2)}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-neutral-600 dark:text-neutral-400">Stocks</span>
              <span className="font-semibold text-neutral-900 dark:text-neutral-100">{mockDividends.length}</span>
            </div>
          </div>
        </div>

        {/* Business Income */}
        <div className="bg-white dark:bg-gray-800 border border-neutral-100 dark:border-neutral-700 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Business Income</h3>
            <Building2 className="w-5 h-5 text-orange-600" />
          </div>
          <div className="space-y-3">
            {mockBusinesses.map((business) => (
              <div key={business.id} className="border-b border-neutral-100 dark:border-neutral-700 pb-3 last:border-b-0">
                <div className="flex justify-between items-start mb-1">
                  <span className="font-medium text-neutral-900 dark:text-neutral-100">{business.name}</span>
                  <span className="text-xs text-neutral-500 dark:text-neutral-400">{business.type.replace('_', ' ')}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600 dark:text-neutral-400">Monthly Revenue</span>
                  <span className="font-semibold text-green-600">{formatCurrency(business.monthly_revenue)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600 dark:text-neutral-400">Profit Margin</span>
                  <span className="font-semibold text-neutral-900 dark:text-neutral-100">{(business.profit_margin * 100).toFixed(1)}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Transactions */}
      <div>
        <h2 className="text-base font-semibold text-neutral-800 dark:text-neutral-200 mb-2">Recent Transactions</h2>
        <div className="bg-white dark:bg-gray-800 border border-neutral-100 dark:border-neutral-700 rounded-lg divide-y divide-neutral-100 dark:divide-neutral-700 shadow-sm">
          {mappedTransactions.map((tx, i) => (
            <div key={i} className="flex items-center gap-4 px-4 py-3">
              <div className={`rounded-full p-2 ${tx.bg}`}><tx.icon className={`w-5 h-5 ${tx.color}`} /></div>
              <div className="flex-1">
                <div className="text-sm font-medium text-neutral-800 dark:text-neutral-200">{tx.label}</div>
                <div className="text-xs text-neutral-400 dark:text-neutral-500">{tx.date}</div>
              </div>
              <div className={`text-sm font-semibold ${tx.amount < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>{tx.amount < 0 ? '-' : '+'}{formatCurrency(Math.abs(tx.amount))}</div>
            </div>
          ))}
        </div>
        <div className="flex justify-end mt-2">
          <button className="text-sm text-emerald-600 dark:text-emerald-400 hover:underline font-medium">View All</button>
        </div>
      </div>

      {/* AI Insight */}
      <div className="bg-white dark:bg-gray-800 border border-neutral-100 dark:border-neutral-700 rounded-lg p-4 shadow-sm flex items-center gap-3">
        <div className="bg-blue-50 dark:bg-blue-900/30 rounded-full p-2"><Lightbulb className="w-5 h-5 text-blue-400 dark:text-blue-300" /></div>
        <div>
          <div className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-1">AI Insight</div>
          <div className="text-xs text-neutral-500 dark:text-neutral-400">Based on your spending patterns, you could save approximately $320 monthly by reducing restaurant expenses. This would accelerate your home down payment goal by 3 months.</div>
        </div>
      </div>
    </div>
  );
}; 